import { useEffect, useRef, useState } from "react";
import { Mic, Square, Send, Loader2, RotateCcw, Award, Volume2, ArrowLeft, Sparkles, Target, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const LANGS = [
  { id: "fr", label: "Français" },
  { id: "en", label: "English" },
  { id: "es", label: "Español" },
];

const DIFF_COLOR = {
  Facile: "bg-green-100 text-green-700",
  Moyen: "bg-amber-100 text-amber-700",
  Difficile: "bg-orange-100 text-orange-700",
  Piège: "bg-red-100 text-red-700",
  "Cas réel": "bg-[#C89A3E]/20 text-[#8A5A1E]",
};

const playB64 = (b64) => {
  if (!b64) return;
  try { new Audio(`data:audio/mpeg;base64,${b64}`).play().catch(() => {}); } catch { /* ignore */ }
};

export const VoiceCoach = ({ initialCase, onCaseConsumed }) => {
  const [personas, setPersonas] = useState([]);
  const [lang, setLang] = useState("fr");
  const [persona, setPersona] = useState(null);
  const [active, setActive] = useState(null); // {kind:'persona'|'case', ref}
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [text, setText] = useState("");
  const [debrief, setDebrief] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);

  useEffect(() => { api.coachPersonas().then(setPersonas).catch(() => {}); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, loading]);

  // Auto-start when launched from a case ("S'entraîner sur ce cas")
  useEffect(() => {
    if (initialCase) {
      beginSession("case", initialCase, { name: initialCase.client_name || "Client", title: initialCase.titre, difficulty: "Cas réel" });
      onCaseConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCase]);

  const beginSession = async (kind, ref, provisional) => {
    setActive({ kind, ref });
    setPersona(provisional);
    setDebrief(null);
    setMessages([]);
    setSession(null);
    setLoading(true);
    try {
      const res = kind === "case"
        ? await api.coachStart(null, lang, ref.id)
        : await api.coachStart(ref.id, lang);
      setSession(res.session_id);
      setPersona(res.persona);
      setMessages([{ role: "client", text: res.client_text, audio: res.audio }]);
      playB64(res.audio);
    } catch {
      toast.error("Impossible de démarrer la simulation.");
      setActive(null);
      setPersona(null);
    } finally {
      setLoading(false);
    }
  };

  const restart = () => active && beginSession(active.kind, active.ref, persona);

  const handleReply = async (fn) => {
    setLoading(true);
    try {
      const res = await fn();
      setMessages((m) => [...m, { role: "advisor", text: res.advisor_text }, { role: "client", text: res.client_text, audio: res.audio }]);
      playB64(res.audio);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Le client virtuel n'a pas pu répondre.");
    } finally {
      setLoading(false);
    }
  };

  const sendText = async () => {
    const t = text.trim();
    if (!t || !session) return;
    setText("");
    await handleReply(() => api.coachReplyText(session, t));
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 800) { toast.error("Aucune parole détectée."); return; }
        await handleReply(() => api.coachReplyAudio(session, blob));
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Micro inaccessible. Autorisez le micro ou utilisez le texte.");
    }
  };

  const stopRec = () => { recRef.current?.stop(); setRecording(false); };

  const runDebrief = async () => {
    setLoading(true);
    try { setDebrief(await api.coachDebrief(session)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bilan indisponible."); }
    finally { setLoading(false); }
  };

  const reset = () => { setActive(null); setPersona(null); setSession(null); setMessages([]); setDebrief(null); setText(""); };

  // --- Persona picker ---
  if (!active) {
    return (
      <div data-testid="voice-coach">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Sparkles size={16} className="text-[#C89A3E]" />
            <span>Parlez à un client virtuel qui vous répond en voix, puis recevez un bilan de coaching IA.</span>
          </div>
          <div className="flex items-center gap-1.5">
            {LANGS.map((l) => (
              <button key={l.id} data-testid={`coach-lang-${l.id}`} onClick={() => setLang(l.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${lang === l.id ? "bg-[#012141] text-white border-[#012141]" : "bg-white text-[#012141] border-[#012141]/15 hover:border-[#C89A3E]"}`}>
                {l.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {personas.map((p) => (
            <button key={p.id} data-testid={`coach-persona-${p.id}`} onClick={() => beginSession("persona", p, p)} disabled={loading}
              className="text-left rounded-2xl border border-[#012141]/12 bg-white p-4 hover:border-[#C89A3E] hover:shadow-md transition-all disabled:opacity-60">
              <div className="flex items-center justify-between gap-2">
                <span className="font-heading font-bold text-[#012141]">{p.name}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${DIFF_COLOR[p.difficulty] || "bg-slate-100 text-slate-600"}`}>{p.difficulty}</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{p.title}</div>
              <p className="text-sm text-slate-600 mt-2">{p.persona}</p>
              <div className="mt-2 text-[11px] text-[#C89A3E] font-semibold flex items-start gap-1"><Target size={12} className="mt-0.5" /> {p.objectif}</div>
            </button>
          ))}
          {personas.length === 0 && <div className="text-slate-400 text-sm py-8 text-center col-span-2">Chargement des clients virtuels…</div>}
        </div>
        <p className="mt-3 text-xs text-slate-400 flex items-center gap-1.5"><ClipboardList size={13} /> Astuce : depuis « Produits & cas », lancez « S'entraîner sur ce cas » pour pratiquer un profil réel du manuel AMF.</p>
      </div>
    );
  }

  // --- Conversation ---
  return (
    <div data-testid="voice-coach-session">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <button data-testid="coach-back" onClick={reset} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#012141]">
          <ArrowLeft size={16} /> Changer de client
        </button>
        <div className="text-right">
          <div className="font-heading font-bold text-[#012141] text-sm">{persona?.name}</div>
          <div className="text-xs text-slate-500">{persona?.title}</div>
        </div>
      </div>

      {persona?.source === "cas" && (persona?.recommendations?.length > 0 || persona?.objectif) && (
        <div data-testid="coach-reco-panel" className="mb-3 rounded-2xl border border-[#C89A3E]/30 bg-[#FDFDFD] p-3">
          {persona?.objectif && <div className="text-xs text-[#012141] mb-1.5 flex items-start gap-1.5"><Target size={13} className="text-[#C89A3E] mt-0.5" /> <b>Objectif :</b> {persona.objectif}</div>}
          {persona?.recommendations?.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Produits à viser</span>
              {persona.recommendations.map((r, i) => <span key={i} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#012141]/8 text-[#012141]">{r}</span>)}
            </div>
          )}
        </div>
      )}

      <div ref={scrollRef} className="rounded-2xl border border-[#012141]/10 bg-[#FDFDFD] p-4 space-y-3 max-h-[40vh] overflow-y-auto">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "advisor" ? "justify-end" : "justify-start"}`}>
            <div data-testid={`coach-msg-${m.role}`} className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "advisor" ? "bg-[#012141] text-white" : "bg-white border border-[#012141]/10 text-[#012141]"}`}>
              <div className="text-[10px] font-bold uppercase tracking-wide opacity-60 mb-0.5">{m.role === "advisor" ? "Vous" : persona?.name}</div>
              <div className="leading-relaxed whitespace-pre-line">{m.text}</div>
              {m.role === "client" && m.audio && (
                <button onClick={() => playB64(m.audio)} className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#C89A3E]"><Volume2 size={12} /> Réécouter</button>
              )}
            </div>
          </div>
        ))}
        {loading && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={15} className="animate-spin" /> …</div>}
      </div>

      {debrief ? (
        <div data-testid="coach-debrief" className="mt-4 rounded-2xl border-2 border-[#C89A3E]/40 bg-white p-5">
          <div className="flex items-center gap-3">
            <div className="grid place-items-center w-14 h-14 rounded-2xl bg-[#012141] text-[#C89A3E]"><Award size={26} /></div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#C89A3E]">Bilan de coaching</div>
              <div className="font-heading text-2xl font-bold text-[#012141]">{debrief.note != null ? `${debrief.note}/10` : "—"}</div>
            </div>
          </div>
          {debrief.verdict && <p className="mt-3 text-sm text-slate-700">{debrief.verdict}</p>}
          {debrief.forces?.length > 0 && (
            <div className="mt-3"><div className="text-xs font-bold text-green-700 mb-1">Forces</div>
              <ul className="space-y-1">{debrief.forces.map((f, i) => <li key={i} className="text-sm text-slate-600 flex gap-2"><span className="text-green-600">✓</span>{f}</li>)}</ul></div>
          )}
          {debrief.ameliorations?.length > 0 && (
            <div className="mt-3"><div className="text-xs font-bold text-orange-700 mb-1">À améliorer</div>
              <ul className="space-y-1">{debrief.ameliorations.map((f, i) => <li key={i} className="text-sm text-slate-600 flex gap-2"><span className="text-orange-500">→</span>{f}</li>)}</ul></div>
          )}
          {debrief.conseil_cle && <div className="mt-3 rounded-xl bg-[#012141]/5 p-3 text-sm text-[#012141]"><b>Conseil clé :</b> {debrief.conseil_cle}</div>}
          {debrief.exemple_reponse && (
            <div data-testid="coach-model-answer" className="mt-3 rounded-xl border border-[#C89A3E]/40 bg-[#C89A3E]/10 p-3.5">
              <div className="text-xs font-bold uppercase tracking-wide text-[#8A5A1E] mb-1.5 flex items-center gap-1.5"><Sparkles size={13} /> Comment j'aurais répondu — à répéter à voix haute</div>
              <p className="text-sm text-[#012141] leading-relaxed italic">« {debrief.exemple_reponse} »</p>
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <button data-testid="coach-restart" onClick={restart} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#C89A3E] text-[#012141] font-bold text-sm"><RotateCcw size={15} /> Recommencer</button>
            <button onClick={reset} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-[#012141]/15 text-[#012141] font-semibold text-sm">Autre client</button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <div className="flex items-center gap-2">
            {!recording ? (
              <button data-testid="coach-record-btn" onClick={startRec} disabled={loading || !session}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#012141] text-white font-semibold hover:bg-[#00172E] disabled:opacity-50">
                <Mic size={18} /> Parler
              </button>
            ) : (
              <button data-testid="coach-stop-btn" onClick={stopRec}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-red-600 text-white font-semibold animate-pulse">
                <Square size={16} /> Arrêter & envoyer
              </button>
            )}
            <div className="flex-1 flex items-center gap-2">
              <input data-testid="coach-text-input" value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendText()} disabled={loading || recording || !session}
                placeholder="…ou tapez votre réplique" className="flex-1 px-4 py-3 rounded-full border border-[#012141]/15 text-sm focus:outline-none focus:border-[#C89A3E]" />
              <button data-testid="coach-send-btn" onClick={sendText} disabled={loading || recording || !text.trim()}
                className="grid place-items-center w-11 h-11 rounded-full bg-[#C89A3E] text-[#012141] disabled:opacity-40"><Send size={17} /></button>
            </div>
          </div>
          <button data-testid="coach-debrief-btn" onClick={runDebrief} disabled={loading || messages.length < 3}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#012141] disabled:opacity-40 hover:text-[#C89A3E]">
            <Award size={15} /> Terminer & voir le bilan de coaching
          </button>
        </div>
      )}
    </div>
  );
};
